import type { DashboardOrderEvent } from '../types/dashboard.types'

export const ERP_FIELD_LABELS: Record<string, string> = {
  status_changed: 'Status',
  delivery_date_changed: 'Leveringsdato',
  info_changed: 'Ordreinfo',
  reference_changed: 'Referanse',
  your_ref_changed: 'Kundens ref',
  shipping_changed: 'Frakt',
  lines_added: 'Varelinjer',
  lines_removed: 'Varelinjer',
  order_created: 'Opprettet',
  order_updated: 'Oppdatert',
  erp_sync: 'ERP-sync',
  erp_created: 'Opprettet i ERP',
}

export function formatEventTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDateShort(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
  })
}

export function formatTimeShort(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDateNice(dateValue: unknown): string {
  if (!dateValue) return ''
  const str = String(dateValue)
  if (/^\d{2}\.\d{2}$/.test(str)) return str
  const date = new Date(str)
  if (isNaN(date.getTime())) return str
  return date.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

interface LineInfo {
  article_no: string
  description: string
  count: number
}

interface ChangedField {
  label: string
  old: string
  new: string
}

export function formatChangeValue(event: DashboardOrderEvent): string {
  const metadata = event.metadata as Record<string, unknown> | undefined
  if (!metadata) return event.content || ''

  const type = event.event_type

  switch (type) {
    case 'status_changed': {
      const oldVal = metadata.old_status || ''
      const newVal = metadata.new_status || ''
      return oldVal ? `${oldVal} → ${newVal}` : `→ ${newVal}`
    }
    case 'shipping_changed':
    case 'reference_changed':
    case 'your_ref_changed': {
      const oldVal = metadata.old_value || ''
      const newVal = metadata.new_value || ''
      return oldVal ? `${oldVal} → ${newVal}` : `→ ${newVal}`
    }
    case 'delivery_date_changed': {
      const oldDate = formatDateNice(metadata.old_date)
      const newDate = formatDateNice(metadata.new_date)
      return oldDate ? `${oldDate} → ${newDate}` : `→ ${newDate}`
    }
    case 'info_changed': {
      const newInfo = String(metadata.new_info || '').slice(0, 60)
      const truncated = (metadata.new_info as string)?.length > 60 ? '...' : ''
      return `→ ${newInfo}${truncated}`
    }
    case 'lines_added': {
      const count = metadata.lines_added as number
      const lines = metadata.lines as LineInfo[] | undefined
      if (lines && lines.length > 0) {
        return lines
          .map((l) => `+${l.count} x ${l.article_no || l.description?.slice(0, 20) || '?'}`)
          .join(', ')
      }
      return `+${count} lagt til`
    }
    case 'lines_removed': {
      const count = metadata.lines_removed as number
      const lines = metadata.lines as LineInfo[] | undefined
      if (lines && lines.length > 0) {
        return lines
          .map((l) => `-${l.count} x ${l.article_no || l.description?.slice(0, 20) || '?'}`)
          .join(', ')
      }
      return `-${count} fjernet`
    }
    case 'order_updated': {
      const changedFields = metadata.changed_fields as Record<string, ChangedField> | undefined
      if (changedFields && Object.keys(changedFields).length > 0) {
        return Object.values(changedFields)
          .map((f) => `${f.label}: ${f.old || '(tom)'} → ${f.new}`)
          .join(', ')
      }
      return event.content || 'Oppdatert'
    }
    default:
      return event.content || ''
  }
}

export function formatOrderDate(dateString: string | null): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
